import { useEffect, useMemo, useRef, useState, createContext, useContext } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { LaunchScreen } from "@/components/loading/LaunchScreen";
import { HandoffPhase, HandoffPhaseContext } from "@/contexts/HandoffPhaseContext";
import { useMarkAuthReady, useMarkShellRender, useLCPObserver } from "@/hooks/usePerfMarks";
import { stopRefreshWatchdog } from "@/utils/pwaRefreshWatchdog";

/**
 * Context to signal child components that handoff is still in progress.
 * During handoff, branding elements (logos) should be hidden to prevent flash.
 */
interface HandoffContextType {
  isHandoffComplete: boolean;
}

const HandoffContext = createContext<HandoffContextType>({ isHandoffComplete: true });

export const useHandoff = () => useContext(HandoffContext);

interface BootstrapGateProps {
  children: React.ReactNode;
}

/**
 * Root-level gate that ensures ONLY LaunchScreen renders until auth bootstrap is complete.
 * Uses atomic handoff to prevent intermediate logo flashes during startup.
 */
export const BootstrapGate = ({ children }: BootstrapGateProps) => {
  const { bootstrapStatus, bootstrapReason, isAuthReady, retryAuthInit } = useAuth();
  const [fadeOut, setFadeOut] = useState(false);
  const [phase, setPhase] = useState<HandoffPhase>("boot");
  const startTs = useMemo(() => performance.now(), []);
  const phaseRef = useRef<HandoffPhase>("boot");
  const completeHandoffTimerRef = useRef<number | null>(null);

  const isHandoffComplete = phase === "complete";

  // Performance marks
  useMarkShellRender();
  useMarkAuthReady(isAuthReady);
  useLCPObserver();

  // Stop the PWA refresh watchdog as soon as BootstrapGate commits a frame.
  // If we're rendering (even just the LaunchScreen) then React is alive and
  // the page is painting — there is no "blank refresh hang" to recover from.
  // Leaving the watchdog armed behind a slow/offline bootstrap caused a
  // reload loop that kept users stuck on the splash screen.
  useEffect(() => {
    const id = requestAnimationFrame(() => stopRefreshWatchdog());
    return () => cancelAnimationFrame(id);
  }, []);

  // Atomic handoff: wait for 2 animation frames after bootstrap ready.
  // IMPORTANT: App stays fully invisible (opacity: 0) until fade finishes.
  useEffect(() => {
    const completeHandoff = () => {
      if (phaseRef.current === "complete") return;
      phaseRef.current = "complete";
      setPhase("complete");
    };

    if (bootstrapStatus !== "ready") {
      if (completeHandoffTimerRef.current) {
        window.clearTimeout(completeHandoffTimerRef.current);
        completeHandoffTimerRef.current = null;
      }
      if (phaseRef.current !== "boot") {
        phaseRef.current = "boot";
        setPhase("boot");
      }
      setFadeOut(false);
      return;
    }

    // Move into handoff phase once bootstrap is ready
    if (phaseRef.current === "boot") {
      phaseRef.current = "handoff";
      setPhase("handoff");
    }

    // Trigger fade immediately - single RAF is enough for paint sync
    if (!fadeOut) {
      requestAnimationFrame(() => {
        setFadeOut(true);
      });
    }

    // Safety net: if the opacity transitionend is skipped/missed on Android
    // PWA cold start, complete the handoff anyway so the app wrapper becomes
    // visible instead of remaining hidden behind the splash indefinitely.
    if (!completeHandoffTimerRef.current) {
      completeHandoffTimerRef.current = window.setTimeout(() => {
        completeHandoffTimerRef.current = null;
        completeHandoff();
      }, 450);
    }

    return () => {
      if (completeHandoffTimerRef.current) {
        window.clearTimeout(completeHandoffTimerRef.current);
        completeHandoffTimerRef.current = null;
      }
    };
  }, [bootstrapStatus, fadeOut]);

  // Determine subtitle based on bootstrap state
  const getSubtitle = () => {
    if (bootstrapStatus === "stalled") {
      return "Taking longer than expected…";
    }
    // Offline first-launch: user has never signed in online before
    if (bootstrapReason === "offline_no_session") {
      return "You're offline. Please connect to the internet and sign in once to enable offline access.";
    }
    return undefined;
  };

  const subtitle = getSubtitle();

  const handleFadeTransitionEnd = (e: React.TransitionEvent) => {
    if (!fadeOut) return;
    if (phaseRef.current === "complete") return;
    if (e.propertyName !== "opacity") return;

    const target = e.target as HTMLElement;
    // Only accept the LaunchScreen FADE LAYER finishing its opacity transition
    // (The backdrop stays fully opaque to prevent any see-through during fade.)
    if (target?.getAttribute("data-testid") !== "launch-screen-fade") return;

    if (completeHandoffTimerRef.current) {
      window.clearTimeout(completeHandoffTimerRef.current);
      completeHandoffTimerRef.current = null;
    }

    phaseRef.current = "complete";
    setPhase("complete");

    // eslint-disable-next-line no-console
    if (import.meta.env.DEV) {
      console.log("[BootstrapGate] handoff_complete", {
        t: Math.round(performance.now() - startTs),
      });
      // Also log globally for prefetch coordination timeline
      console.log("[Prefetch] handoff_complete", performance.now().toFixed(1) + 'ms');
    }
  };

  return (
    <HandoffPhaseContext.Provider value={{ phase }}>
      <HandoffContext.Provider value={{ isHandoffComplete }}>
        {/* App stays fully invisible (opacity:0) until handoff is COMPLETE (after fade).
            CRITICAL: No transition on this wrapper to prevent GPU layer promotion during handoff. */}
        <div
          data-testid="bootstrap-gate-wrapper"
          style={{
            opacity: isHandoffComplete ? 1 : 0,
            pointerEvents: isHandoffComplete ? "auto" : "none",
            // Explicitly prevent any transform/will-change from being inherited
            transform: 'none',
            willChange: 'auto',
          }}
        >
          {children}
        </div>

        {/* LaunchScreen overlay remains fully opaque until fade begins. */}
        {!isHandoffComplete && (
          <div className="fixed inset-0 z-[9999]" onTransitionEnd={handleFadeTransitionEnd}>
            <LaunchScreen 
              subtitle={subtitle} 
              fadingOut={fadeOut}
              onRetry={bootstrapStatus === 'stalled' ? retryAuthInit : undefined}
            />
          </div>
        )}
      </HandoffContext.Provider>
    </HandoffPhaseContext.Provider>
  );
};
